import { useEffect, useState } from 'react';
import UpsertNetworkModal from '../../../components/modals/networks/UpsertNetworkModal';
import { Button } from '@oc-labs/wt-storybook';
import { Eye, EyeClosed, Plus } from "lucide-react";
import useModal from '../../../hooks/useModal';
import useNetworks from '../../../hooks/useNetworks';
import toast from 'react-hot-toast';
import DeleteModal from '../../../components/modals/DeleteModal';

export default function AdminNetworks() {
  const { adminNetworks, adminDeleteNetwork } = useNetworks();
  const { open: openCreateModal } = useModal('create-network-modal');
  const { open: openDeleteModal } = useModal('delete-modal');
  const [networks, setNetworks] = useState([]);

  useEffect(() => {
    loadNetworks();
  }, []);

  const loadNetworks = () => adminNetworks()
    .then((res) => setNetworks(res))
    .catch((err) => toast.error(err.message || 'Failed to load networks'));

  const handleRemoveNetwork = (networkId) => {
    openDeleteModal({
      title: 'Delete Network',
      text: 'Are you sure you want to delete this network? This action cannot be undone.',
      onDelete: async () => {
        try {
          await adminDeleteNetwork(networkId);
          toast.success('Network deleted successfully');
          loadNetworks();
        } catch (error) {
          toast.error(error.message || 'Failed to delete network');
        }
      }
    });
  }

  return (<>
    <UpsertNetworkModal onUpsert={loadNetworks} />
    <DeleteModal />
    <div className="2xl:p-6 p-3">

      <div className='flex justify-between items-center mb-4'>
        <h2 className="2xl:text-xl text-base font-medium">All networks</h2>
        <div className='flex'>
          <Button
            type="secondary"
            size="small"
            onClick={() => openCreateModal()}
            icon={<Plus size={16} />}
            className="whitespace-nowrap"
          >
            Create Network
          </Button>
        </div>
      </div>
      <div className="rounded-lg shadow overflow-hidden">
        <table className="w-full bg-white table-fixed">
          <thead className="bg-gray-100 uppercase">
            <tr>
              <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[6%]">Active</th>
              <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[14%]">Name</th>
              <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[14%]">ID</th>
              <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[8%]">Chain ID</th>
              <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[22%]">RPC URL</th>
              <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[22%]">Explorer URL</th>
              <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[14%]"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {networks.map((network) => (
              <tr key={network.id} className="hover:bg-gray-50 transition">
                <td className="2xl:py-4 py-2.5 2xl:px-6 px-3">
                  <div className='flex items-center gap-2'>
                    <div className='rounded-full w-4 h-4 bg-gray-200 flex items-center justify-center shrink-0'>
                      <div className={`rounded-full w-2 h-2 ${network.enabled ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    </div>
                    <div>
                      {network.displayDefault ? <Eye className="text-gray-400 w-4" /> : <EyeClosed className="text-gray-400 w-4" />}
                    </div>
                  </div>
                </td>
                <td className="2xl:py-4 py-2.5 2xl:px-6 px-3 text-xs truncate">{network.name}</td>
                <td className="2xl:py-4 py-2.5 2xl:px-6 px-3 text-xs truncate">{network.id}</td>
                <td className="2xl:py-4 py-2.5 2xl:px-6 px-3 text-xs truncate">{network.chainId}</td>
                <td className="2xl:py-4 py-2.5 2xl:px-6 px-3 text-xs truncate">{network.rpcUrl}</td>
                <td className="2xl:py-4 py-2.5 2xl:px-6 px-3 text-xs truncate">{network.explorerUrl}</td>
                <td className="2xl:py-4 py-2.5 2xl:px-6 px-3 text-xs">
                  <div className='flex items-center gap-2'>
                    <Button
                      onClick={() => openCreateModal(network)}
                      size="small"
                      type="primary"
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleRemoveNetwork(network.id)}
                      size="small"
                      type="danger"
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </>);
}
